import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Modal, Button, Form, FormGroup, FormLabel, FormError, Input, Badge } from '@/components/ui';
import type { Company, SubscriptionPlanCode } from '../types';

const companySchema = z.object({
  name: z.string().min(2, 'Tên công ty phải có ít nhất 2 ký tự'),
  code: z.string().min(2, 'Mã công ty phải có ít nhất 2 ký tự'),
  email: z.string().email('Email không hợp lệ').optional().or(z.literal('')),
  phone: z.string().optional().or(z.literal('')),
  address: z.string().optional().or(z.literal('')),
  city: z.string().optional().or(z.literal('')),
  website: z.string().url('Website không hợp lệ').optional().or(z.literal('')),
  subscription_plan_code: z.enum(['free', 'basic', 'pro', 'enterprise']),
  is_active: z.boolean(),
  max_projects: z.number().min(0),
  max_users: z.number().min(0),
  max_assets: z.number().min(0),
});

type CompanyFormValues = z.infer<typeof companySchema>;

interface CompanyFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Partial<Company>) => Promise<any>;
  isLoading?: boolean;
  company?: Company | null;
}

export default function CompanyFormModal({ isOpen, onClose, onSave, isLoading, company }: CompanyFormModalProps) {
  const { register, handleSubmit, formState: { errors }, reset } = useForm<CompanyFormValues>({
    resolver: zodResolver(companySchema),
    defaultValues: company ? {
      name: company.name,
      code: company.code,
      email: company.email || '',
      phone: company.phone || '',
      address: company.address || '',
      city: company.city || '',
      website: company.website || '',
      subscription_plan_code: company.subscription_plan_code,
      is_active: company.is_active,
      max_projects: company.max_projects,
      max_users: company.max_users,
      max_assets: company.max_assets,
    } : {
      subscription_plan_code: 'free',
      is_active: true,
      max_projects: 3,
      max_users: 10,
      max_assets: 100,
    }
  });

  const onSubmit = async (data: CompanyFormValues) => {
    const res = await onSave(company ? { ...data, id: company.id } : data);
    if (!res.error) {
      onClose();
      reset();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={company ? 'Hiệu chỉnh Tenant' : 'Khởi tạo Tenant mới'}
      size="lg"
      className="p-0 overflow-hidden"
    >
      <div className="bg-slate-900 px-8 py-10 relative overflow-hidden">
        <div className="relative z-10">
          <Badge variant="custom" className="bg-amber-400/20 text-amber-400 border-amber-400/30 mb-3 font-black text-[10px] tracking-widest ">
            Platform Master Control
          </Badge>
          <h2 className="text-2xl font-black text-white tracking-tight">
            {company ? `Cấu hình: ${company.name}` : 'Thiết lập Tổ chức mới'}
          </h2>
          <p className="text-slate-400 text-sm mt-1 font-medium">Cấu hình tham số hệ thống và hạn mức tài nguyên cho tenant.</p>
        </div>
        <i className="ri-building-4-line absolute -bottom-8 -right-8 text-[160px] text-white/5" />
      </div>

      <Form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-8 bg-os-canvas">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Identification Section */}
          <section className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shadow-sm">
                <i className="ri-fingerprint-line" />
              </div>
              <h4 className="text-xs font-black text-slate-400  tracking-widest">Định danh hệ thống</h4>
            </div>

            <FormGroup>
              <FormLabel required className="text-slate-600 font-bold">Tên thương hiệu / Công ty</FormLabel>
              <Input
                {...register('name')}
                error={errors.name?.message}
                placeholder="VD: Landscape International"
                className="h-12 rounded-xl border-slate-200 focus:border-brand-500 shadow-sm"
              />
            </FormGroup>

            <FormGroup>
              <FormLabel required className="text-slate-600 font-bold">Mã định danh (Unique Code)</FormLabel>
              <Input
                {...register('code')}
                error={errors.code?.message}
                placeholder="VD: GO_INT"
                disabled={!!company}
                className="h-12 rounded-xl border-slate-200 font-mono font-bold bg-slate-50 shadow-sm"
              />
            </FormGroup>
          </section>

          {/* Configuration Section */}
          <section className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center shadow-sm">
                <i className="ri-settings-3-line" />
              </div>
              <h4 className="text-xs font-black text-slate-400  tracking-widest">Cấu hình vận hành</h4>
            </div>

            <FormGroup>
              <FormLabel className="text-slate-600 font-bold">Gói dịch vụ đăng ký</FormLabel>
              <div className="relative">
                <select
                  {...register('subscription_plan_code')}
                  className="w-full h-12 px-4 pr-10 rounded-xl border border-slate-200 focus:border-brand-500 focus:ring-4 focus:ring-brand-500/5 outline-none transition-all bg-white font-bold text-slate-700 shadow-sm appearance-none"
                >
                  <option value="free">Standard (Free)</option>
                  <option value="basic">Startup (Basic)</option>
                  <option value="pro">Enterprise (Professional)</option>
                  <option value="enterprise">Unlimited (Custom)</option>
                </select>
                <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              </div>
            </FormGroup>

            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-between group hover:border-brand-200 transition-colors">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${register('is_active') ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-200 text-slate-400'
                  }`}>
                  <i className="ri-shield-check-line text-xl" />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900">Kích hoạt Tenant</p>
                  <p className="text-[10px] text-slate-400 font-medium">Cho phép truy cập hệ thống</p>
                </div>
              </div>
              <input
                type="checkbox"
                {...register('is_active')}
                className="w-6 h-6 rounded-lg border-slate-300 text-brand-600 focus:ring-brand-500 cursor-pointer transition-transform active:scale-90"
              />
            </div>
          </section>
        </div>

        {/* Resources Section */}
        <section className="p-6 bg-slate-900 rounded-[32px] relative overflow-hidden">
          <div className="relative z-10">
            <h4 className="text-xs font-black text-amber-400  tracking-widest mb-6 flex items-center gap-2">
              <i className="ri-database-2-line" />
              Hạn mức tài nguyên hệ thống (Quotas)
            </h4>
            <div className="grid grid-cols-3 gap-6">
              {[
                { name: 'max_projects', label: 'Max Projects', icon: 'ri-stack-line' },
                { name: 'max_users', label: 'Max Users', icon: 'ri-group-line' },
                { name: 'max_assets', label: 'Max Assets', icon: 'ri-plant-line' },
              ].map(field => (
                <FormGroup key={field.name}>
                  <FormLabel className="text-[10px] font-black text-slate-400  tracking-tighter mb-2 block">{field.label}</FormLabel>
                  <div className="relative">
                    <Input
                      type="number"
                      {...register(field.name as any, { valueAsNumber: true })}
                      error={(errors as any)[field.name]?.message}
                      className="h-14 rounded-2xl border-white/10 bg-white/5 text-white font-black text-xl text-center focus:bg-white/10 focus:border-amber-400 transition-all pl-10"
                    />
                    <i className={`${field.icon} absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 text-lg`} />
                  </div>
                </FormGroup>
              ))}
            </div>
          </div>
          <div className="absolute top-0 right-0 w-32 h-32 bg-amber-400/5 rounded-full -mr-16 -mt-16 blur-2xl" />
        </section>

        {/* Footer Actions */}
        <div className="flex gap-4 pt-4">
          <Button
            variant="ghost"
            className="flex-1 h-14 rounded-2xl font-bold text-slate-500 hover:bg-slate-100"
            onClick={onClose}
            type="button"
          >
            Hủy bỏ
          </Button>
          <Button
            variant="primary"
            className="flex-1 h-14 rounded-2xl font-black  tracking-widest shadow-xl shadow-brand-500/20"
            isLoading={isLoading}
            type="submit"
          >
            {company ? 'Lưu thay đổi Master' : 'Khởi tạo Tenant'}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
